const phoneBrowserMatrix = [
  ['galaxy-a14', 'chrome'],
  ['galaxy-a14', 'samsung-internet'],
  ['pixel-7', 'chrome'],
  ['pixel-7', 'samsung-internet'],
];

const checklistKeys = [
  'actualPrintedArtwork',
  'completeTargetSet',
  'completeVideoSet',
  'presentationModes',
  'priorityChanges',
  'concurrentDifferentVideos',
  'sameVideoPlaybackPosition',
  'looping',
  'lossAndResume',
  'rangeSeekAndResume',
  'networkInterruption',
  'backgroundForeground',
  'repeatedSessionCleanup',
  'embeddedEntryAndHandoff',
];

const checklistStates = new Set(['not-run', 'observed', 'passed', 'gap']);
const evidenceKeys = [
  'schemaVersion', 'runId', 'seriesId', 'capturedAt', 'device', 'browser', 'build', 'fixture',
  'conditions', 'trial', 'timeline', 'checklist', 'measurements', 'observations', 'privacy',
];
const forbiddenEvidenceKeys = new Set([
  'authorization', 'cookie', 'cookies', 'bearer', 'token', 'sessiontoken', 'sas', 'sasurl',
  'signedurl', 'asseturl', 'mediaurl', 'videourl', 'cameraimage', 'cameraframe', 'screenshot',
]);

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function nonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function isIsoDateTime(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(value) &&
    Number.isFinite(Date.parse(value));
}

/** Returns which captured/manual signal proves playback, or null when a run has no usable evidence. */
export function playbackEvidenceSource(run) {
  const diagnostics = run?.measurements?.diagnostics;
  const hasFrame = player => player?.playingEvents >= 1 && player?.decodedFrames >= 1 &&
    player?.widthPx > 0 && player?.heightPx > 0;
  const playedPlayers = [...new Map((diagnostics?.videoPlayers ?? [])
    .filter(player => nonEmptyString(player?.videoId) && hasFrame(player))
    .map(player => [player.videoId, player])).values()];
  const expectedConcurrent = run?.fixture?.concurrentVideosExpected;
  if (expectedConcurrent > 1) {
    if (playedPlayers.length >= expectedConcurrent &&
        diagnostics?.concurrentPlayingCountHighWater >= expectedConcurrent &&
        diagnostics?.concurrentPlaybackDurationsByCount?.some(entry =>
          entry?.minimumVideoCount === expectedConcurrent && entry.durationMs >= 1000)) {
      return 'telemetry-concurrent-distinct-videos';
    }
    return null;
  }
  if (playedPlayers.length >= 1) return 'telemetry-per-video';
  if (diagnostics?.videoPlayingEvents >= 1 && diagnostics?.decodedVideoFrames >= 1 &&
      diagnostics?.videoResolutionPx?.widthPx > 0 && diagnostics?.videoResolutionPx?.heightPx > 0) {
    return 'telemetry-selected-video';
  }
  if (Number.isFinite(run?.measurements?.manual?.videoStartupMs) &&
      run.measurements.manual.videoStartupMs >= 0 && Array.isArray(run?.timeline) &&
      run.timeline.some(event => event?.event === 'video-started')) {
    return 'manual-single-video-start';
  }
  return null;
}

function optionalFinite(value) {
  return value === null || (typeof value === 'number' && Number.isFinite(value));
}

function optionalFiniteNonNegative(value) {
  return value === null || (typeof value === 'number' && Number.isFinite(value) && value >= 0);
}

function allowedKeys(value, keys, path, errors) {
  if (!isPlainObject(value)) return;
  for (const key of Object.keys(value)) {
    if (!keys.includes(key)) errors.push(`${path}.${key} is not an allowed evidence field.`);
  }
}

function containsSensitiveMaterial(value) {
  if (typeof value === 'string') {
    const compact = value.trim();
    return /\bBearer\s+\S+/i.test(value) ||
      /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/.test(value) ||
      /data:(?:image|video)\//i.test(value) ||
      /(?:^|\s)(?:\/9j\/[A-Za-z0-9+/]{16,}|iVBORw0KGgo[A-Za-z0-9+/]{12,}|R0lGOD[A-Za-z0-9+/]{12,})/.test(value) ||
      /^[A-Za-z0-9+/_=-]{256,}$/.test(compact) ||
      /https?:\/\/[^\s"'<>]+[?&](?:sig|se|sp|sv)=/i.test(value);
  }
  if (Array.isArray(value)) return value.some(containsSensitiveMaterial);
  if (!isPlainObject(value)) return false;
  return Object.entries(value).some(([key, item]) =>
    forbiddenEvidenceKeys.has(key.toLowerCase().replaceAll(/[^a-z]/g, '')) || containsSensitiveMaterial(item));
}

function validateResolution(value, path, errors, {allowZero = false} = {}) {
  if (value === null) return;
  if (!isPlainObject(value) || !Number.isInteger(value.widthPx) || !Number.isInteger(value.heightPx) ||
      value.widthPx < (allowZero ? 0 : 1) || value.heightPx < (allowZero ? 0 : 1)) {
    errors.push(`${path} must contain ${allowZero ? 'non-negative' : 'positive'} integer widthPx/heightPx or be null.`);
    return;
  }
  allowedKeys(value, ['widthPx', 'heightPx'], path, errors);
}

function validateChecklist(checklist, errors) {
  if (!isPlainObject(checklist)) {
    errors.push('checklist must be an object.');
    return;
  }
  for (const key of checklistKeys) {
    const item = checklist[key];
    if (!isPlainObject(item) || !checklistStates.has(item.state)) {
      errors.push(`checklist.${key}.state must be not-run, observed, passed, or gap.`);
      continue;
    }
    allowedKeys(item, ['state', 'note'], `checklist.${key}`, errors);
    if (item.note !== undefined && typeof item.note !== 'string') {
      errors.push(`checklist.${key}.note must be a string when supplied.`);
    }
    if (item.state === 'gap' && !nonEmptyString(item.note)) {
      errors.push(`checklist.${key}.note is required when the state is gap.`);
    }
    if (['observed', 'passed'].includes(item.state) && !nonEmptyString(item.note)) {
      errors.push(`checklist.${key}.note is required when the state is ${item.state}.`);
    }
  }
  for (const key of Object.keys(checklist)) {
    if (!checklistKeys.includes(key)) errors.push(`checklist.${key} is not a recognized qualification item.`);
  }
}

/**
 * Validate one memory-only device trial. This validates evidence shape/privacy,
 * not whether the measured runtime is good enough to release.
 */
export function validateQualificationRun(run) {
  const errors = [];
  if (!isPlainObject(run)) return ['run must be an object.'];
  if (containsSensitiveMaterial(run)) {
    errors.push('evidence must not include credentials, signed asset URLs, or camera captures.');
  }
  allowedKeys(run, evidenceKeys, 'run', errors);

  if (run.schemaVersion !== 5) {
    errors.push('schemaVersion must be 5; earlier versions must be recaptured (version 4 recorded the retired API build check and target preparation tool, and versions 1 through 3 predate the measured concurrent-overlap duration contract).');
  }
  if (typeof run.runId !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(run.runId)) {
    errors.push('runId must be a UUID.');
  }
  if (typeof run.seriesId !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(run.seriesId)) {
    errors.push('seriesId must be a UUID shared by repeat trials of the same setup.');
  }
  if (!isIsoDateTime(run.capturedAt)) errors.push('capturedAt must be an ISO date-time with a timezone.');

  const device = run.device;
  allowedKeys(device, ['family', 'model', 'variant', 'osVersion', 'buildNumber'], 'device', errors);
  if (!isPlainObject(device) || !['galaxy-a14', 'pixel-7', 'other'].includes(device.family) ||
      !nonEmptyString(device.model) || !nonEmptyString(device.variant) ||
      !nonEmptyString(device.osVersion) || !nonEmptyString(device.buildNumber)) {
    errors.push('device must identify the model, variant, verified OS version, and build.');
  }

  const browser = run.browser;
  allowedKeys(browser, ['family', 'name', 'version', 'embeddedHost'], 'browser', errors);
  if (!isPlainObject(browser) || !['chrome', 'samsung-internet', 'embedded-other', 'other'].includes(browser.family) ||
      !nonEmptyString(browser.name) || !nonEmptyString(browser.version)) {
    errors.push('browser must identify its family, installed name, and version.');
  }
  if (browser?.family === 'embedded-other' && !nonEmptyString(browser.embeddedHost)) {
    errors.push('browser.embeddedHost is required for embedded-browser trials.');
  }
  if (browser && browser.family !== 'embedded-other' && browser.embeddedHost !== undefined && browser.embeddedHost !== null) {
    errors.push('browser.embeddedHost is only valid for an embedded browser.');
  }

  const build = run.build;
  allowedKeys(build, ['viewerBuildId', 'runtimeVersion', 'engineVersion'], 'build', errors);
  const revisionPattern = /^[0-9a-f]{40}(?:[0-9a-f]{24})?$/i;
  if (!isPlainObject(build) || !revisionPattern.test(build.viewerBuildId ?? '') ||
      !nonEmptyString(build.runtimeVersion) || !nonEmptyString(build.engineVersion)) {
    errors.push('build must contain the full Viewer revision and runtime/engine versions.');
  }

  const fixture = run.fixture;
  allowedKeys(fixture, ['identifier', 'contentRevision', 'printLabel', 'expectedTargets', 'loadedTargets',
    'expectedVideos', 'loadedVideos', 'concurrentVideosExpected', 'presentationModes'], 'fixture', errors);
  if (!isPlainObject(fixture) || !/^[A-Za-z0-9]{10}$/.test(fixture.identifier ?? '') ||
      !nonEmptyString(fixture.contentRevision) || !nonEmptyString(fixture.printLabel) ||
      !Number.isInteger(fixture.expectedTargets) || fixture.expectedTargets < 1 ||
      !Number.isInteger(fixture.loadedTargets) || fixture.loadedTargets < 0 ||
      !Number.isInteger(fixture.expectedVideos) || fixture.expectedVideos < 1 ||
      !Number.isInteger(fixture.loadedVideos) || fixture.loadedVideos < 0 ||
      !Number.isInteger(fixture.concurrentVideosExpected) || fixture.concurrentVideosExpected < 1 ||
      fixture.concurrentVideosExpected > fixture.expectedVideos ||
      !Array.isArray(fixture.presentationModes) || fixture.presentationModes.length === 0 ||
      fixture.presentationModes.some(mode => !['fit', 'fill', 'center', 'legacy'].includes(mode))) {
    errors.push('fixture must include its published code/revision, print, complete target/video counts, a concurrent-video expectation from 1 through expectedVideos, and presentation modes.');
  }

  const conditions = run.conditions;
  allowedKeys(conditions, ['entryMethod', 'measurementPurpose', 'network', 'lighting', 'screencastingEnabled',
    'renderCameraTexture', 'cameraSetting', 'cameraResolutionPx', 'cameraResolutionSource', 'cameraTrackResolutionPx',
    'printDistanceCm', 'viewingAngleDegrees', 'externalCameraUsed'], 'conditions', errors);
  if (!isPlainObject(conditions) || !['direct', 'embedded', 'external-handoff', 'other'].includes(conditions.entryMethod) ||
      !['performance', 'visual-observation'].includes(conditions.measurementPurpose) ||
      !nonEmptyString(conditions.network) || !nonEmptyString(conditions.lighting) ||
      typeof conditions.screencastingEnabled !== 'boolean' || typeof conditions.renderCameraTexture !== 'boolean' ||
      typeof conditions.externalCameraUsed !== 'boolean' || !nonEmptyString(conditions.cameraSetting)) {
    errors.push('conditions must record entry, trial purpose, network, lighting, camera/render settings, and external-camera use.');
  }
  if (conditions?.measurementPurpose === 'performance' && conditions.screencastingEnabled) {
    errors.push('performance trials require remote screencasting to be off.');
  }
  if (conditions?.measurementPurpose === 'performance' && conditions.externalCameraUsed) {
    errors.push('external-camera visual observations must use a separate visual-observation trial.');
  }
  for (const key of ['printDistanceCm', 'viewingAngleDegrees']) {
    if (conditions && !optionalFiniteNonNegative(conditions[key])) {
      errors.push(`conditions.${key} must be a non-negative number or null.`);
    }
  }
  if (conditions && !['xr8-video-size', 'media-track-settings', 'unavailable', null].includes(conditions.cameraResolutionSource)) {
    errors.push('conditions.cameraResolutionSource is invalid.');
  }
  if (conditions) {
    validateResolution(conditions.cameraResolutionPx, 'conditions.cameraResolutionPx', errors);
    validateResolution(conditions.cameraTrackResolutionPx, 'conditions.cameraTrackResolutionPx', errors);
  }

  const trial = run.trial;
  allowedKeys(trial, ['startedAt', 'endedAt', 'state', 'phase'], 'trial', errors);
  if (!isPlainObject(trial) || !isIsoDateTime(trial.startedAt) || !isIsoDateTime(trial.endedAt) ||
      Date.parse(trial.endedAt) < Date.parse(trial.startedAt) ||
      !['completed', 'incomplete'].includes(trial.state) ||
      !['baseline', 'tuned', 'follow-up'].includes(trial.phase)) {
    errors.push('trial must have ordered ISO start/end times, a completed/incomplete state, and a baseline/tuned/follow-up phase.');
  }

  const timelineEvents = new Set(['trial-start', 'camera-ready', 'first-aligned-frame', 'target-lost',
    'target-reacquired', 'video-started', 'video-rebuffered', 'stationary-window-marked', 'trial-end']);
  if (!Array.isArray(run.timeline)) {
    errors.push('timeline must be an array of manually marked events.');
  } else {
    run.timeline.forEach((event, index) => {
      if (!isPlainObject(event) || !timelineEvents.has(event.event) ||
          !optionalFiniteNonNegative(event.elapsedMs) || event.elapsedMs === null || !isIsoDateTime(event.at) ||
          (event.note !== undefined && typeof event.note !== 'string')) {
        errors.push(`timeline[${index}] is malformed.`);
      } else {
        allowedKeys(event, ['event', 'elapsedMs', 'at', 'note'], `timeline[${index}]`, errors);
      }
    });
  }
  if (Array.isArray(run.timeline)) {
    const startMarks = run.timeline.filter(event => event?.event === 'trial-start');
    const endMarks = run.timeline.filter(event => event?.event === 'trial-end');
    if (startMarks.length !== 1 || run.timeline[0]?.event !== 'trial-start') {
      errors.push('timeline must begin with exactly one trial-start mark.');
    }
    if (endMarks.length !== 1 || run.timeline.at(-1)?.event !== 'trial-end') {
      errors.push('timeline must end with exactly one trial-end mark.');
    }
    for (let index = 1; index < run.timeline.length; index++) {
      const previous = run.timeline[index - 1];
      const current = run.timeline[index];
      if (isIsoDateTime(previous?.at) && isIsoDateTime(current?.at) && Date.parse(current.at) < Date.parse(previous.at)) {
        errors.push(`timeline[${index}].at precedes the previous event.`);
      }
      if (typeof previous?.elapsedMs === 'number' && typeof current?.elapsedMs === 'number' && current.elapsedMs < previous.elapsedMs) {
        errors.push(`timeline[${index}].elapsedMs precedes the previous event.`);
      }
    }
    const firstAlignedMarks = run.timeline.filter(event => event?.event === 'first-aligned-frame');
    const manualAlignmentMs = run.measurements?.manual?.beginToFirstAlignedFrameMs;
    if (firstAlignedMarks.length > 1 || (firstAlignedMarks.length === 1) !== (manualAlignmentMs !== null && manualAlignmentMs !== undefined)) {
      errors.push('the manual first-aligned-overlay event and its elapsed-time measurement must appear together at most once.');
    } else if (firstAlignedMarks.length === 1 && typeof firstAlignedMarks[0].elapsedMs === 'number' &&
        typeof manualAlignmentMs === 'number' && Math.abs(firstAlignedMarks[0].elapsedMs - manualAlignmentMs) > 0.2) {
      errors.push('manual aligned-overlay elapsed time must match the timeline event.');
    }
    if (isPlainObject(trial) && isIsoDateTime(trial.startedAt) && isIsoDateTime(trial.endedAt) && startMarks.length === 1 && endMarks.length === 1) {
      if (Date.parse(startMarks[0].at) < Date.parse(trial.startedAt) || Date.parse(endMarks[0].at) > Date.parse(trial.endedAt)) {
        errors.push('timeline trial marks must fall within the recorded trial start/end times.');
      }
    }
  }

  validateChecklist(run.checklist, errors);
  if (isPlainObject(fixture) && isPlainObject(run.checklist)) {
    for (const [expected, loaded, itemKey] of [
      ['expectedTargets', 'loadedTargets', 'completeTargetSet'],
      ['expectedVideos', 'loadedVideos', 'completeVideoSet'],
    ]) {
      if (Number.isInteger(fixture[expected]) && Number.isInteger(fixture[loaded]) && fixture[expected] !== fixture[loaded]) {
        const item = run.checklist[itemKey];
        if (!['gap', 'observed'].includes(item?.state) || !nonEmptyString(item?.note)) {
          errors.push(`fixture count mismatch must be explained as observed or gap in checklist.${itemKey}.`);
        }
      }
    }
  }

  const measurements = run.measurements;
  allowedKeys(measurements, ['diagnostics', 'manual'], 'measurements', errors);
  if (!isPlainObject(measurements) || !isPlainObject(measurements.diagnostics) || !isPlainObject(measurements.manual)) {
    errors.push('measurements must contain diagnostics and manual observations.');
  } else {
    const diagnostics = measurements.diagnostics;
    const diagnosticKeys = ['state', 'platform', 'targetSetReady', 'targetAcquisitions', 'targetLosses',
      'snapshotAt', 'viewerStateAtSnapshot', 'sessionGeneration', 'sessionStartsDuringTrial', 'sessionStartedAt', 'sessionRunningAt',
      'viewerStateAtTrialStart', 'sessionGenerationAtTrialStart',
      'processedFreshFrameCount', 'processedFreshFramesPerSecond', 'xrUpdateCallbacks', 'xrUpdateCallbacksPerSecond',
      'targetFoundToFirstFreshXrFrameMs', 'lastFrameRepeatStatus', 'cameraResolutionSource', 'cameraTrackStatus',
      'videoWaitingEvents', 'videoPlayingEvents', 'videoErrorEvents', 'decodedVideoFrames', 'droppedVideoFrames',
      'cameraResolutionPx', 'cameraTrackResolutionPx', 'videoResolutionPx', 'videoPlayers',
      'concurrentPlayingCountHighWater', 'concurrentPlaybackDurationMs', 'concurrentPlaybackDurationsByCount', 'relativePose'];
    allowedKeys(diagnostics, diagnosticKeys, 'measurements.diagnostics', errors);
    if (!['captured', 'unavailable', 'not-run'].includes(diagnostics.state)) {
      errors.push('measurements.diagnostics.state must be captured, unavailable, or not-run.');
    }
    if (!isIsoDateTime(diagnostics.snapshotAt)) errors.push('measurements.diagnostics.snapshotAt must record when the diagnostic snapshot was frozen.');
    if (isIsoDateTime(diagnostics.snapshotAt) && isIsoDateTime(trial?.endedAt) &&
        Date.parse(diagnostics.snapshotAt) < Date.parse(trial.endedAt)) {
      errors.push('measurements.diagnostics.snapshotAt cannot precede the recorded End Run time.');
    }
    if (isIsoDateTime(diagnostics.snapshotAt) && isIsoDateTime(run.capturedAt) &&
        Date.parse(diagnostics.snapshotAt) > Date.parse(run.capturedAt)) {
      errors.push('measurements.diagnostics.snapshotAt cannot follow the evidence export time.');
    }
    const viewerStates = ['idle', 'preparing', 'prepared', 'starting', 'running', 'paused', 'failed', 'stopped', 'unavailable'];
    for (const key of ['viewerStateAtSnapshot', 'viewerStateAtTrialStart']) {
      if (diagnostics[key] !== null && !viewerStates.includes(diagnostics[key])) {
        errors.push(`measurements.diagnostics.${key} is invalid.`);
      }
    }
    for (const key of ['sessionStartedAt', 'sessionRunningAt']) {
      if (diagnostics[key] !== null && !isIsoDateTime(diagnostics[key])) {
        errors.push(`measurements.diagnostics.${key} must be an ISO date-time or null.`);
      }
    }
    if (diagnostics.sessionGeneration !== null && (!Number.isInteger(diagnostics.sessionGeneration) || diagnostics.sessionGeneration < 0)) {
      errors.push('measurements.diagnostics.sessionGeneration must be a non-negative integer or null.');
    }
    if (diagnostics.sessionGenerationAtTrialStart !== null &&
        (!Number.isInteger(diagnostics.sessionGenerationAtTrialStart) || diagnostics.sessionGenerationAtTrialStart < 0)) {
      errors.push('measurements.diagnostics.sessionGenerationAtTrialStart must be a non-negative integer or null.');
    }
    if (diagnostics.sessionStartsDuringTrial !== null &&
        (!Number.isInteger(diagnostics.sessionStartsDuringTrial) || diagnostics.sessionStartsDuringTrial < 0)) {
      errors.push('measurements.diagnostics.sessionStartsDuringTrial must be a non-negative integer or null.');
    }
    if (diagnostics.state === 'captured') {
      if (!Number.isInteger(diagnostics.sessionGeneration) || !isIsoDateTime(diagnostics.sessionStartedAt) ||
          !isIsoDateTime(diagnostics.sessionRunningAt) || !Number.isInteger(diagnostics.sessionGenerationAtTrialStart) ||
          diagnostics.sessionStartsDuringTrial !== 1) {
        errors.push('captured diagnostics must identify exactly one fresh Viewer session launched during this trial.');
      } else if (Date.parse(diagnostics.sessionRunningAt) < Date.parse(diagnostics.sessionStartedAt)) {
        errors.push('captured diagnostics sessionRunningAt cannot precede sessionStartedAt.');
      } else {
        const startedFreshDuringTrial = isIsoDateTime(trial?.startedAt) && isIsoDateTime(trial?.endedAt) &&
          diagnostics.sessionGeneration === diagnostics.sessionGenerationAtTrialStart + 1 &&
          !['running', 'paused'].includes(diagnostics.viewerStateAtTrialStart) &&
          Date.parse(diagnostics.sessionStartedAt) >= Date.parse(trial.startedAt) &&
          Date.parse(diagnostics.sessionStartedAt) <= Date.parse(trial.endedAt) &&
          Date.parse(diagnostics.sessionRunningAt) >= Date.parse(diagnostics.sessionStartedAt) &&
          Date.parse(diagnostics.sessionRunningAt) <= Date.parse(trial.endedAt);
        if (!startedFreshDuringTrial) {
          errors.push('captured diagnostics must come from one fresh Viewer session started and running within the trial; prior or multiple sessions are not performance evidence.');
        }
      }
    }
    for (const key of ['platform', 'cameraResolutionSource', 'cameraTrackStatus']) {
      if (diagnostics[key] !== null && typeof diagnostics[key] !== 'string') {
        errors.push(`measurements.diagnostics.${key} must be a string or null.`);
      }
    }
    for (const key of ['processedFreshFrameCount', 'processedFreshFramesPerSecond', 'xrUpdateCallbacks',
      'xrUpdateCallbacksPerSecond', 'targetFoundToFirstFreshXrFrameMs', 'targetAcquisitions', 'targetLosses',
      'videoWaitingEvents', 'videoPlayingEvents', 'videoErrorEvents', 'decodedVideoFrames', 'droppedVideoFrames',
      'concurrentPlayingCountHighWater', 'concurrentPlaybackDurationMs']) {
      if (!optionalFiniteNonNegative(diagnostics[key])) errors.push(`measurements.diagnostics.${key} must be a non-negative number or null.`);
    }
    if (!Array.isArray(diagnostics.concurrentPlaybackDurationsByCount)) {
      errors.push('measurements.diagnostics.concurrentPlaybackDurationsByCount must be an array.');
    } else {
      const seenVideoCounts = new Set();
      diagnostics.concurrentPlaybackDurationsByCount.forEach((entry, index) => {
        const path = `measurements.diagnostics.concurrentPlaybackDurationsByCount[${index}]`;
        allowedKeys(entry, ['minimumVideoCount', 'durationMs'], path, errors);
        if (!isPlainObject(entry) || !Number.isInteger(entry.minimumVideoCount) || entry.minimumVideoCount < 2 ||
            entry.minimumVideoCount > diagnostics.concurrentPlayingCountHighWater ||
            entry.minimumVideoCount > fixture?.expectedVideos) {
          errors.push(`${path}.minimumVideoCount must be an integer from 2 through both the expected video count and measured concurrent-playing high-water mark.`);
          return;
        }
        if (seenVideoCounts.has(entry.minimumVideoCount)) errors.push(`${path}.minimumVideoCount must be unique.`);
        seenVideoCounts.add(entry.minimumVideoCount);
        if (!optionalFiniteNonNegative(entry.durationMs)) errors.push(`${path}.durationMs must be a non-negative number or null.`);
      });
    }
    if (diagnostics.targetSetReady !== null && typeof diagnostics.targetSetReady !== 'boolean') {
      errors.push('measurements.diagnostics.targetSetReady must be a boolean or null.');
    }
    if (diagnostics.lastFrameRepeatStatus !== null && !['fresh', 'repeat', 'unavailable'].includes(diagnostics.lastFrameRepeatStatus)) {
      errors.push('measurements.diagnostics.lastFrameRepeatStatus is invalid.');
    }
    for (const key of ['cameraResolutionPx', 'cameraTrackResolutionPx', 'videoResolutionPx']) {
      validateResolution(diagnostics[key], `measurements.diagnostics.${key}`, errors, {allowZero: true});
    }
    if (!Array.isArray(diagnostics.videoPlayers)) {
      errors.push('measurements.diagnostics.videoPlayers must be an array.');
    } else {
      const videoPlayerKeys = ['videoId', 'widthPx', 'heightPx', 'durationSeconds', 'positionSeconds', 'paused', 'muted',
        'waitingEvents', 'playingEvents', 'errorEvents', 'decodedFrames', 'droppedFrames', 'urlRenewals', 'lastUrlRenewalAtMs'];
      diagnostics.videoPlayers.forEach((player, index) => {
        const path = `measurements.diagnostics.videoPlayers[${index}]`;
        allowedKeys(player, videoPlayerKeys, path, errors);
        if (!isPlainObject(player) || (player.videoId !== null && !nonEmptyString(player.videoId))) {
          errors.push(`${path} must contain a valid videoId or null.`);
          return;
        }
        for (const key of ['widthPx', 'heightPx', 'durationSeconds', 'positionSeconds', 'waitingEvents', 'playingEvents',
          'errorEvents', 'decodedFrames', 'droppedFrames', 'urlRenewals', 'lastUrlRenewalAtMs']) {
          if (!optionalFiniteNonNegative(player[key])) errors.push(`${path}.${key} must be a non-negative number or null.`);
        }
        for (const key of ['paused', 'muted']) {
          if (player[key] !== null && typeof player[key] !== 'boolean') errors.push(`${path}.${key} must be a boolean or null.`);
        }
      });
    }
    if (diagnostics.relativePose !== null) {
      const pose = diagnostics.relativePose;
      allowedKeys(pose, ['marked', 'windowDurationMs', 'samples', 'translationDeltaMeanXrUnits',
        'translationDeltaMaxXrUnits', 'rotationDeltaMeanDegrees', 'rotationDeltaMaxDegrees', 'interpretation'],
      'measurements.diagnostics.relativePose', errors);
      if (!isPlainObject(pose) || typeof pose.marked !== 'boolean' ||
          !optionalFiniteNonNegative(pose.windowDurationMs) || !optionalFiniteNonNegative(pose.samples) ||
          !optionalFiniteNonNegative(pose.translationDeltaMeanXrUnits) || !optionalFiniteNonNegative(pose.translationDeltaMaxXrUnits) ||
          !optionalFiniteNonNegative(pose.rotationDeltaMeanDegrees) || !optionalFiniteNonNegative(pose.rotationDeltaMaxDegrees) ||
          !nonEmptyString(pose.interpretation)) {
        errors.push('measurements.diagnostics.relativePose is malformed.');
      }
    }

    const manual = measurements.manual;
    allowedKeys(manual, ['beginToFirstAlignedFrameMs', 'alignedOverlayTimingMethod',
      'registrationOffsetHorizontalMm', 'registrationOffsetVerticalMm', 'registrationOffsetReference',
      'registrationOffsetMethod', 'videoStartupMs', 'concurrentVideosObserved', 'falsePositiveCount', 'rebufferCount', 'tracking', 'lag',
      'glareAndOcclusion', 'thermal', 'memory', 'failures', 'tuningChanges', 'qualityRating'],
    'measurements.manual', errors);
    for (const key of ['beginToFirstAlignedFrameMs', 'videoStartupMs', 'falsePositiveCount', 'rebufferCount']) {
      if (!optionalFiniteNonNegative(manual[key])) errors.push(`measurements.manual.${key} must be a non-negative number or null.`);
    }
    for (const key of ['registrationOffsetHorizontalMm', 'registrationOffsetVerticalMm']) {
      if (!optionalFinite(manual[key])) errors.push(`measurements.manual.${key} must be a signed number or null.`);
    }
    if (manual.concurrentVideosObserved !== null &&
        (!Number.isInteger(manual.concurrentVideosObserved) || manual.concurrentVideosObserved < 0 ||
          manual.concurrentVideosObserved > fixture?.expectedVideos || manual.concurrentVideosObserved > fixture?.loadedVideos)) {
      errors.push('measurements.manual.concurrentVideosObserved must be a non-negative integer no greater than expectedVideos or loadedVideos, or null.');
    }
    for (const key of ['alignedOverlayTimingMethod', 'registrationOffsetReference', 'registrationOffsetMethod',
      'tracking', 'lag', 'glareAndOcclusion', 'thermal', 'memory', 'failures', 'tuningChanges']) {
      if (manual[key] !== null && typeof manual[key] !== 'string') errors.push(`measurements.manual.${key} must be a string or null.`);
    }
    if (manual.beginToFirstAlignedFrameMs !== null && !nonEmptyString(manual.alignedOverlayTimingMethod)) {
      errors.push('manual begin-to-first-aligned-overlay timing requires a method description.');
    }
    const videoStartedMarks = Array.isArray(run.timeline) ? run.timeline.filter(event => event?.event === 'video-started') : [];
    if (manual.videoStartupMs !== null && videoStartedMarks.length === 0) {
      errors.push('manual video startup duration requires a video-started timeline mark.');
    } else if (videoStartedMarks.length > 0 && typeof manual.videoStartupMs === 'number' &&
        typeof videoStartedMarks[0].elapsedMs === 'number' && Math.abs(videoStartedMarks[0].elapsedMs - manual.videoStartupMs) > 0.2) {
      errors.push('manual video startup duration must match the first video-started timeline event.');
    }
    const hasOffset = manual.registrationOffsetHorizontalMm !== null || manual.registrationOffsetVerticalMm !== null;
    if (hasOffset && (!nonEmptyString(manual.registrationOffsetReference) || !nonEmptyString(manual.registrationOffsetMethod))) {
      errors.push('manual registration offsets require a reference and measurement method.');
    }
    if (manual.qualityRating !== null && !['unrated', 'poor', 'fair', 'good', 'excellent'].includes(manual.qualityRating)) {
      errors.push('measurements.manual.qualityRating is invalid.');
    }
    if (manual.concurrentVideosObserved !== null) {
      const item = run.checklist?.concurrentDifferentVideos;
      const note = item?.note ?? '';
      const count = manual.concurrentVideosObserved;
      const namesCount = Number.isInteger(count) && new RegExp(`\\b${count}\\s+(?:distinct\\s+)?videos?\\b`, 'i').test(note);
      const describesConcurrency = /\b(simultaneously|concurrently|at the same time)\b/i.test(note);
      const allowedStates = count >= (fixture?.concurrentVideosExpected ?? Infinity)
        ? ['observed', 'passed'] : ['observed', 'gap'];
      if (!nonEmptyString(note) || !namesCount || !describesConcurrency || !allowedStates.includes(item?.state)) {
        errors.push('a manual concurrent-video count requires a matching observed count, simultaneous-playback note, and observed/passed state (or observed/gap when below the expectation).');
      }
    }
    if (conditions?.measurementPurpose === 'performance' &&
        (manual.beginToFirstAlignedFrameMs !== null || hasOffset ||
          (Array.isArray(run.timeline) && run.timeline.some(event => event?.event === 'first-aligned-frame')))) {
      errors.push('manual overlay alignment and print-edge registration observations belong in a separate visual-observation trial.');
    }
    if (conditions?.measurementPurpose === 'performance' && trial?.phase === 'tuned' && trial?.state === 'completed' &&
        !nonEmptyString(manual.tuningChanges)) {
      errors.push('a completed tuned performance trial must describe the tuning change from baseline.');
    }
  }

  if (typeof run.observations !== 'string') errors.push('observations must be a string.');
  const privacy = run.privacy;
  allowedKeys(privacy, ['cameraFramesStored', 'signedMediaUrlsIncluded', 'sessionCredentialsIncluded', 'uploaded'], 'privacy', errors);
  if (!isPlainObject(privacy) || privacy.cameraFramesStored !== false || privacy.signedMediaUrlsIncluded !== false ||
      privacy.sessionCredentialsIncluded !== false || privacy.uploaded !== false) {
    errors.push('privacy must confirm camera frames, signed URLs, credentials, and uploads are absent.');
  }

  return errors;
}

/** Summarize evidence presence only; a complete matrix is still a human release decision. */
export function summarizeDeviceMatrix(runs) {
  const errors = [];
  if (!Array.isArray(runs)) return {errors: ['runs must be an array.'], cells: [], coverageComplete: false, releaseDecision: 'human-review-required'};
  const ids = new Set();
  for (const [index, run] of runs.entries()) {
    for (const error of validateQualificationRun(run)) errors.push(`runs[${index}]: ${error}`);
    if (typeof run?.runId === 'string') {
      if (ids.has(run.runId)) errors.push(`runs[${index}]: runId is duplicated.`);
      ids.add(run.runId);
    }
  }

  const cells = phoneBrowserMatrix.map(([device, browser]) => {
    const matching = runs.filter(run => run?.device?.family === device && run?.browser?.family === browser);
    const performanceRuns = matching.filter(run => run?.conditions?.measurementPurpose === 'performance' && run?.trial?.state === 'completed');
    const phases = new Set(performanceRuns.map(run => run?.trial?.phase));
    const baselinePresent = phases.has('baseline');
    const tunedPresent = phases.has('tuned');
    const targetVideoComplete = run => run?.fixture?.expectedTargets >= 1 && run?.fixture?.expectedVideos >= 1 &&
      run?.fixture?.expectedTargets === run?.fixture?.loadedTargets &&
      run?.fixture?.expectedVideos === run?.fixture?.loadedVideos;
    const checklistRecorded = run => isPlainObject(run?.checklist) && checklistKeys.every(key =>
      ['observed', 'passed', 'gap'].includes(run.checklist[key]?.state) &&
      nonEmptyString(run.checklist[key]?.note));
    const hasCapturedMeasurements = run => run?.measurements?.diagnostics?.state === 'captured' &&
      run.measurements.diagnostics.sessionStartsDuringTrial === 1 &&
      run.measurements.diagnostics.sessionGeneration === run.measurements.diagnostics.sessionGenerationAtTrialStart + 1 &&
      !['running', 'paused'].includes(run.measurements.diagnostics.viewerStateAtTrialStart) &&
      isIsoDateTime(run.trial?.startedAt) && isIsoDateTime(run.trial?.endedAt) &&
      Date.parse(run.measurements.diagnostics.sessionStartedAt) >= Date.parse(run.trial.startedAt) &&
      Date.parse(run.measurements.diagnostics.sessionRunningAt) <= Date.parse(run.trial.endedAt) &&
      run.measurements.diagnostics.processedFreshFrameCount >= 1 &&
      Number.isFinite(run.measurements.diagnostics.processedFreshFramesPerSecond) &&
      run.measurements.diagnostics.xrUpdateCallbacks >= 1 &&
      Number.isFinite(run.measurements.diagnostics.xrUpdateCallbacksPerSecond) &&
      run.measurements.diagnostics.cameraResolutionPx?.widthPx > 0 &&
      run.measurements.diagnostics.cameraResolutionPx?.heightPx > 0 &&
      isIsoDateTime(run.measurements.diagnostics.snapshotAt) &&
      isIsoDateTime(run.measurements.diagnostics.sessionRunningAt);
    const completeCandidates = performanceRuns.filter(run => targetVideoComplete(run) && checklistRecorded(run) &&
      hasCapturedMeasurements(run) &&
      playbackEvidenceSource(run) !== null &&
      (run.trial.phase !== 'tuned' || nonEmptyString(run.measurements?.manual?.tuningChanges)));
    const baselineCandidates = completeCandidates.filter(run => run.trial.phase === 'baseline');
    const tunedCandidates = completeCandidates.filter(run => run.trial.phase === 'tuned');
    const sameSetup = (baseline, tuned) => baseline.seriesId === tuned.seriesId &&
      ['model', 'variant', 'osVersion', 'buildNumber'].every(key => baseline.device[key] === tuned.device[key]) &&
      baseline.browser.version === tuned.browser.version &&
      ['viewerBuildId', 'runtimeVersion', 'engineVersion']
        .every(key => baseline.build[key] === tuned.build[key]) &&
      ['identifier', 'contentRevision', 'printLabel', 'expectedTargets', 'loadedTargets', 'expectedVideos', 'loadedVideos', 'concurrentVideosExpected']
        .every(key => baseline.fixture[key] === tuned.fixture[key]) &&
      JSON.stringify([...baseline.fixture.presentationModes].sort()) === JSON.stringify([...tuned.fixture.presentationModes].sort()) &&
      ['entryMethod', 'network', 'lighting', 'printDistanceCm', 'viewingAngleDegrees']
        .every(key => baseline.conditions[key] === tuned.conditions[key]) &&
      nonEmptyString(tuned.measurements?.manual?.tuningChanges);
    const pairedSetups = new Map();
    for (const baseline of baselineCandidates) {
      for (const tuned of tunedCandidates) {
        if (!sameSetup(baseline, tuned)) continue;
        const changedConfigurationFields = [];
        if (baseline.conditions.cameraSetting !== tuned.conditions.cameraSetting) changedConfigurationFields.push('cameraSetting');
        if (baseline.conditions.renderCameraTexture !== tuned.conditions.renderCameraTexture) changedConfigurationFields.push('renderCameraTexture');
        if (JSON.stringify(baseline.conditions.cameraResolutionPx) !== JSON.stringify(tuned.conditions.cameraResolutionPx)) {
          changedConfigurationFields.push('cameraResolutionPx');
        }
        pairedSetups.set(baseline.seriesId, {
          seriesId: baseline.seriesId,
          baselinePlaybackEvidenceSource: playbackEvidenceSource(baseline),
          tunedPlaybackEvidenceSource: playbackEvidenceSource(tuned),
          concurrentVideosExpected: baseline.fixture.concurrentVideosExpected,
          baselineConfiguration: {
            cameraSetting: baseline.conditions.cameraSetting,
            renderCameraTexture: baseline.conditions.renderCameraTexture,
            cameraResolutionPx: baseline.conditions.cameraResolutionPx,
            cameraResolutionSource: baseline.conditions.cameraResolutionSource,
          },
          tunedConfiguration: {
            cameraSetting: tuned.conditions.cameraSetting,
            renderCameraTexture: tuned.conditions.renderCameraTexture,
            cameraResolutionPx: tuned.conditions.cameraResolutionPx,
            cameraResolutionSource: tuned.conditions.cameraResolutionSource,
          },
          changedConfigurationFields,
          tuningExplanationRecorded: nonEmptyString(tuned.measurements?.manual?.tuningChanges),
        });
      }
    }
    const completeBaseline = baselineCandidates.length > 0;
    const completeTuned = tunedCandidates.length > 0;
    const pairedBaselineTuned = pairedSetups.size > 0;
    return {
      device,
      browser,
      runCount: matching.length,
      seriesCount: new Set(matching.map(run => run?.seriesId).filter(Boolean)).size,
      performanceRunCount: performanceRuns.length,
      visualObservationRunCount: matching.filter(run => run?.conditions?.measurementPurpose === 'visual-observation').length,
      baselinePresent,
      tunedPresent,
      completeBaseline,
      completeTuned,
      pairedBaselineTuned,
      pairedSeriesCount: pairedSetups.size,
      pairedTuning: [...pairedSetups.values()],
      checklistGapItems: [...new Set(matching.flatMap(run => checklistKeys.filter(key => run?.checklist?.[key]?.state === 'gap')))].sort(),
      state: matching.length === 0 ? 'pending' : pairedBaselineTuned ? 'evidence-present' : 'partial',
    };
  });
  return {
    errors,
    cells,
    coverageComplete: errors.length === 0 && cells.every(cell => cell.pairedBaselineTuned),
    releaseDecision: 'human-review-required',
  };
}

export const qualificationChecklistKeys = [...checklistKeys];
export const qualificationPhoneBrowserMatrix = phoneBrowserMatrix.map(([device, browser]) => ({device, browser}));
